function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function pickText(...values) {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function getItemDetails(line = {}) {
  return (
    line.item ??
    line.item_details ??
    line.item_detail ??
    line.item_data ??
    line.itemDetails ??
    line.product ??
    line.product_details ??
    null
  );
}

function canonicalVariant(variant = {}) {
  const variantId = pickText(variant.variantId, variant.variant_id, variant.id, variant.value);
  return {
    variantId,
    variantName: pickText(
      variant.variantName,
      variant.variant_name,
      variant.name,
      variant.label,
      variant.value,
      'Variant',
    ),
    groupId: pickText(variant.groupId, variant.variant_group_id, variant.group_id, variant.group),
    groupName: pickText(
      variant.groupName,
      variant.variant_group_name,
      variant.group_name,
      variant.groupLabel,
    ),
    priceDelta: firstNumber(variant.priceDelta, variant.price_delta, variant.delta_price, 0) ?? 0,
  };
}

function canonicalModifier(modifier = {}) {
  const modifierId = pickText(
    modifier.modifierId,
    modifier.modifier_id,
    modifier.id,
    modifier.value,
  );
  const quantity = firstNumber(
    modifier.quantity,
    modifier.qty,
    modifier.count,
    modifier.selected_quantity,
    1,
  );

  return {
    modifierId,
    modifierName: pickText(
      modifier.modifierName,
      modifier.modifier_name,
      modifier.name,
      modifier.label,
      modifier.value,
      'Modifier',
    ),
    groupId: pickText(
      modifier.groupId,
      modifier.modifier_group_id,
      modifier.group_id,
      modifier.group,
    ),
    groupName: pickText(
      modifier.groupName,
      modifier.modifier_group_name,
      modifier.group_name,
      modifier.groupLabel,
    ),
    quantity: Math.max(1, quantity ?? 1),
    priceDelta:
      firstNumber(modifier.priceDelta, modifier.price_delta, modifier.delta_price, 0) ?? 0,
    parentModifierId: pickText(modifier.parentModifierId, modifier.parent_modifier_id),
    upsellItemId: pickText(modifier.upsellItemId, modifier.upsell_item_id),
    upsellGroupId: pickText(modifier.upsellGroupId, modifier.upsell_group_id),
  };
}

function normalizeVariantList(line = {}) {
  const selectedVariants = asArray(line.selectedVariants).map(canonicalVariant);
  if (selectedVariants.length > 0) return selectedVariants;

  if (line.variant) return [canonicalVariant(line.variant)];

  return [];
}

function normalizeModifierList(line = {}) {
  const selectedModifiers = asArray(line.selectedModifiers).map(canonicalModifier);
  if (selectedModifiers.length > 0) return selectedModifiers;

  return asArray(line.modifiers).map(canonicalModifier);
}

function canonicalComboSelection(selection = {}) {
  return {
    groupId: pickText(selection.groupId, selection.group_id),
    groupName: pickText(selection.groupName, selection.group_name),
    itemId: pickText(selection.itemId, selection.item_id),
    itemName: pickText(selection.itemName, selection.item_name),
    quantity: firstNumber(selection.quantity, 1) ?? 1,
    priceDelta: firstNumber(selection.priceDelta, selection.price_delta, 0) ?? 0,
    parentItemId: pickText(selection.parentItemId, selection.parent_item_id),
    modifiers: asArray(selection.modifiers).map(canonicalModifier),
    variants: asArray(selection.variants).map(canonicalVariant),
  };
}

function normalizeComboSelectionList(line = {}) {
  const list = asArray(line.comboSelections).length
    ? line.comboSelections
    : asArray(line.selectedComboItems);
  return asArray(list).map(canonicalComboSelection);
}

function canonicalIngredient(ingredient = {}) {
  return {
    ingredientId: pickText(ingredient.ingredientId, ingredient.ingredient_id),
    name: pickText(ingredient.name),
    variantId: pickText(ingredient.variantId, ingredient.variant_id),
    variantGroupId: pickText(ingredient.variantGroupId, ingredient.variant_group_id),
  };
}

function findLocationPriceSource(raw, locationId) {
  if (!Array.isArray(raw?.locations) || raw.locations.length === 0) return null;
  if (!locationId) return raw.locations[0];
  return raw.locations.find((location) => location?.location_id === locationId) ?? raw.locations[0];
}

function findChannelPriceSource(location, channelId) {
  if (!Array.isArray(location?.channels) || location.channels.length === 0) return null;
  if (!channelId) return location.channels[0];
  return (
    location.channels.find((channel) => channel?.channel_id === channelId) ?? location.channels[0]
  );
}

function resolveServiceItemPrice(serviceItem = {}) {
  const details =
    serviceItem.service_item_details ??
    serviceItem.serviceItemDetails ??
    serviceItem.details ??
    serviceItem;
  const location = findLocationPriceSource(
    details,
    serviceItem.locationId ?? serviceItem.location_id,
  );
  const channel = findChannelPriceSource(location, serviceItem.channelId ?? serviceItem.channel_id);
  const serviceItemPriceDelta =
    firstNumber(
      serviceItem.priceDelta,
      serviceItem.price_delta,
      details?.priceDelta,
      details?.price_delta,
      0,
    ) ?? 0;
  const channelPrice =
    firstNumber(
      channel?.price,
      channel?.selling_price,
      channel?.priceDelta,
      channel?.price_delta,
      location?.price,
      details?.selling_price,
      details?.price,
      0,
    ) ?? 0;

  return serviceItemPriceDelta + channelPrice;
}

function canonicalServiceItem(serviceItem = {}, group = {}) {
  const details =
    serviceItem.service_item_details ??
    serviceItem.serviceItemDetails ??
    serviceItem.details ??
    serviceItem;
  return {
    serviceItemId: pickText(
      serviceItem.serviceItemId,
      serviceItem.service_item_id,
      details?.service_item_id,
      details?.serviceItemId,
      serviceItem.id,
      details?.id,
    ),
    name: pickText(
      serviceItem.name,
      serviceItem.service_item_name,
      details?.name,
      details?.service_item_name,
      'Service item',
    ),
    variantId: pickText(
      serviceItem.variantId,
      serviceItem.variant_id,
      details?.variant_id,
      details?.variantId,
    ),
    variantGroupId: pickText(
      serviceItem.variantGroupId,
      serviceItem.variant_group_id,
      details?.variant_group_id,
      details?.variantGroupId,
    ),
    serviceItemGroupId: pickText(
      serviceItem.serviceItemGroupId,
      serviceItem.service_item_group_id,
      details?.service_item_group_id,
      details?.serviceItemGroupId,
      group.serviceItemGroupId,
      group.service_item_group_id,
      group.id,
    ),
    groupName: pickText(
      serviceItem.groupName,
      serviceItem.service_item_group_name,
      group.groupName,
      group.name,
      group.service_item_group_name,
    ),
    priceDelta: resolveServiceItemPrice(serviceItem),
    alternativeUnit: pickText(
      serviceItem.alternativeUnit,
      serviceItem.alternative_unit,
      details?.alternative_unit,
      details?.alternativeUnit,
    ),
    conversionRate:
      firstNumber(
        serviceItem.conversionRate,
        serviceItem.conversion_rate,
        details?.conversion_rate,
        details?.conversionRate,
      ) ?? undefined,
  };
}

function normalizeIngredientList(line = {}) {
  return asArray(line.removedIngredients).map(canonicalIngredient);
}

function normalizeServiceItemList(line = {}) {
  const directItems = asArray(line.serviceItems).length
    ? asArray(line.serviceItems)
    : asArray(line.service_items);
  const groupedItems = asArray(line.serviceItemGroups).length
    ? asArray(line.serviceItemGroups)
    : asArray(line.service_item_groups);

  return [
    ...directItems.map((item) => canonicalServiceItem(item)),
    ...groupedItems.flatMap((group) => {
      const items = asArray(group.serviceItems).length
        ? asArray(group.serviceItems)
        : asArray(group.service_items);
      return items.map((item) => canonicalServiceItem(item, group));
    }),
  ];
}

function normalizeAlternativeUnit(line = {}) {
  const details =
    line.alternativeUnitConversion ??
    line.alternativeUnitDetails ??
    line.alternative_unit_details ??
    line.alternative_units_and_conversions?.[0] ??
    null;
  const selectedUomId = pickText(line.selectedUomId, line.selected_uom_id, line.uom_id);
  const alternativeUnit = pickText(
    line.alternativeUnit,
    line.alternative_unit,
    selectedUomId,
    details?.uom_id,
    details?.uomId,
    details?.alternative_uom_id,
    details?.alternativeUomId,
    details?.id,
    details?.unit_details?.uom_id,
    details?.unitDetails?.uomId,
    details?.unit,
  );

  if (!alternativeUnit && !details) return null;

  return {
    selectedUomId,
    alternativeUnit,
    unit: pickText(details?.unit, details?.unit_details?.uom_id, details?.unitDetails?.uomId),
    uom: pickText(details?.uom, details?.uom_details?.uom_id, details?.uomDetails?.uomId),
    conversionRate:
      firstNumber(
        line.conversionRate,
        line.conversion_rate,
        details?.conversion_rate,
        details?.conversionRate,
        0,
      ) ?? 0,
    priceDelta: firstNumber(details?.price_delta, details?.priceDelta, 0) ?? 0,
    barcode: pickText(details?.barcode),
  };
}

function getModifierGroups(item) {
  return asArray(
    item?.modifier_groups?.length ? item.modifier_groups : item?.modifier_group_details,
  );
}

function getSingleModifiers(item) {
  return asArray(
    item?.single_modifier_details?.length ? item.single_modifier_details : item?.single_modifiers,
  );
}

function getVariantGroups(item) {
  return asArray(
    item?.variant_group_details?.length ? item.variant_group_details : item?.variant_groups,
  );
}

function resolveVariantFromItem(item, variant = {}) {
  const variantId = variant.variantId ?? variant.variant_id ?? variant.id ?? variant.value ?? '';
  if (!item || !variantId) return variant;

  for (const group of getVariantGroups(item)) {
    const options = asArray(group?.variants ?? group?.items ?? group?.options ?? group);
    const match = options.find(
      (option) =>
        pickText(option?.variant_id, option?.id, option?.value) === variantId ||
        pickText(option?.variant_name, option?.name, option?.label) === variant.variantName,
    );
    if (!match) continue;
    return {
      ...variant,
      variantName: pickText(
        match.variant_name,
        match.name,
        match.label,
        variant.variantName,
        variantId,
      ),
      groupId:
        variant.groupId || pickText(group.variant_group_id, group.group_id, group.id, group.value),
      groupName:
        variant.groupName ||
        pickText(group.name, group.label, group.variant_group_name, group.group_name),
    };
  }

  return variant;
}

function resolveModifierFromItem(item, modifier = {}) {
  const modifierId =
    modifier.modifierId ?? modifier.modifier_id ?? modifier.id ?? modifier.value ?? '';
  if (!item || !modifierId) return modifier;

  for (const option of getSingleModifiers(item)) {
    if (
      pickText(option?.modifier_id, option?.id, option?.value) === modifierId ||
      pickText(option?.modifier_name, option?.name, option?.label) === modifier.modifierName
    ) {
      return {
        ...modifier,
        modifierName: pickText(
          option?.modifier_name,
          option?.name,
          option?.label,
          modifier.modifierName,
          modifierId,
        ),
        groupId:
          modifier.groupId ||
          pickText(option?.modifier_group_id, option?.group_id, option?.group, option?.groupId),
        groupName:
          modifier.groupName ||
          pickText(
            option?.modifier_group_name,
            option?.group_name,
            option?.groupLabel,
            option?.groupName,
          ),
      };
    }
  }

  for (const group of getModifierGroups(item)) {
    const options = asArray(group?.modifiers ?? group?.items ?? group?.options ?? group);
    const match = options.find(
      (option) =>
        pickText(option?.modifier_id, option?.id, option?.value) === modifierId ||
        pickText(option?.modifier_name, option?.name, option?.label) === modifier.modifierName,
    );
    if (!match) continue;
    return {
      ...modifier,
      modifierName: pickText(
        match.modifier_name,
        match.name,
        match.label,
        modifier.modifierName,
        modifierId,
      ),
      groupId:
        modifier.groupId ||
        pickText(group.modifier_group_id, group.group_id, group.id, group.value),
      groupName:
        modifier.groupName ||
        pickText(group.name, group.label, group.modifier_group_name, group.group_name),
    };
  }

  return modifier;
}

function sortSelections(a, b) {
  const aGroup = `${a.groupId ?? ''}:${a.parentModifierId ?? ''}:${a.variantId ?? a.modifierId ?? ''}:${a.upsellItemId ?? ''}`;
  const bGroup = `${b.groupId ?? ''}:${b.parentModifierId ?? ''}:${b.variantId ?? b.modifierId ?? ''}:${b.upsellItemId ?? ''}`;
  return aGroup.localeCompare(bGroup);
}

function sortComboSelections(a, b) {
  const aKey = `${a.groupId ?? ''}:${a.itemId ?? ''}`;
  const bKey = `${b.groupId ?? ''}:${b.itemId ?? ''}`;
  return aKey.localeCompare(bKey);
}

function sortIngredients(a, b) {
  const aKey = `${a.variantId ?? ''}:${a.ingredientId ?? ''}`;
  const bKey = `${b.variantId ?? ''}:${b.ingredientId ?? ''}`;
  return aKey.localeCompare(bKey);
}

function comboSelectionSignature(line = {}) {
  const combos = normalizeComboSelectionList(line)
    .slice()
    .sort(sortComboSelections)
    .map((selection) => ({
      groupId: selection.groupId ?? '',
      itemId: selection.itemId ?? '',
      quantity: selection.quantity ?? 1,
      priceDelta: selection.priceDelta ?? 0,
      parentItemId: selection.parentItemId ?? '',
      modifiers: selection.modifiers
        .slice()
        .sort(sortSelections)
        .map((m) => ({
          modifierId: m.modifierId ?? '',
          parentModifierId: m.parentModifierId ?? '',
          upsellItemId: m.upsellItemId ?? '',
          upsellGroupId: m.upsellGroupId ?? '',
          quantity: m.quantity ?? 1,
        })),
      variants: selection.variants
        .slice()
        .sort(sortSelections)
        .map((v) => ({ variantId: v.variantId ?? '' })),
    }));

  return JSON.stringify({ combos });
}

function ingredientSelectionSignature(line = {}) {
  const ingredients = normalizeIngredientList(line)
    .slice()
    .sort(sortIngredients)
    .map((ingredient) => ({
      ingredientId: ingredient.ingredientId ?? '',
      variantId: ingredient.variantId ?? '',
      variantGroupId: ingredient.variantGroupId ?? '',
    }));

  return JSON.stringify({ ingredients });
}

function serviceItemSelectionSignature(line = {}) {
  const serviceItems = normalizeServiceItemList(line)
    .slice()
    .sort((a, b) => {
      const aKey = `${a.variantGroupId ?? ''}:${a.variantId ?? ''}:${a.alternativeUnit ?? ''}:${a.serviceItemGroupId ?? ''}:${a.serviceItemId ?? ''}`;
      const bKey = `${b.variantGroupId ?? ''}:${b.variantId ?? ''}:${b.alternativeUnit ?? ''}:${b.serviceItemGroupId ?? ''}:${b.serviceItemId ?? ''}`;
      return aKey.localeCompare(bKey);
    })
    .map((serviceItem) => ({
      serviceItemId: serviceItem.serviceItemId ?? '',
      variantId: serviceItem.variantId ?? '',
      variantGroupId: serviceItem.variantGroupId ?? '',
      serviceItemGroupId: serviceItem.serviceItemGroupId ?? '',
      alternativeUnit: serviceItem.alternativeUnit ?? '',
      conversionRate: serviceItem.conversionRate ?? '',
      priceDelta: serviceItem.priceDelta ?? 0,
    }));

  return JSON.stringify({ serviceItems });
}

function selectionSignature(line = {}) {
  const variants = normalizeVariantList(line)
    .slice()
    .sort(sortSelections)
    .map((variant) => ({
      groupId: variant.groupId ?? '',
      variantId: variant.variantId ?? '',
      priceDelta: variant.priceDelta ?? 0,
    }));
  const modifiers = normalizeModifierList(line)
    .slice()
    .sort(sortSelections)
    .map((modifier) => ({
      groupId: modifier.groupId ?? '',
      modifierId: modifier.modifierId ?? '',
      parentModifierId: modifier.parentModifierId ?? '',
      upsellItemId: modifier.upsellItemId ?? '',
      upsellGroupId: modifier.upsellGroupId ?? '',
      quantity: modifier.quantity ?? 1,
      priceDelta: modifier.priceDelta ?? 0,
    }));

  return JSON.stringify({
    variants,
    modifiers,
    alternativeUnit: normalizeAlternativeUnit(line),
    serviceItems: normalizeServiceItemList(line)
      .slice()
      .sort((a, b) => {
        const aKey = `${a.variantGroupId ?? ''}:${a.variantId ?? ''}:${a.alternativeUnit ?? ''}:${a.serviceItemGroupId ?? ''}:${a.serviceItemId ?? ''}`;
        const bKey = `${b.variantGroupId ?? ''}:${b.variantId ?? ''}:${b.alternativeUnit ?? ''}:${b.serviceItemGroupId ?? ''}:${b.serviceItemId ?? ''}`;
        return aKey.localeCompare(bKey);
      })
      .map((serviceItem) => ({
        serviceItemId: serviceItem.serviceItemId ?? '',
        variantId: serviceItem.variantId ?? '',
        variantGroupId: serviceItem.variantGroupId ?? '',
        serviceItemGroupId: serviceItem.serviceItemGroupId ?? '',
        alternativeUnit: serviceItem.alternativeUnit ?? '',
        conversionRate: serviceItem.conversionRate ?? '',
        priceDelta: serviceItem.priceDelta ?? 0,
      })),
  });
}

export function getCartLineSelections(line = {}) {
  const item = getItemDetails(line);
  return {
    variants: normalizeVariantList(line).map((variant) => resolveVariantFromItem(item, variant)),
    modifiers: normalizeModifierList(line).map((modifier) =>
      resolveModifierFromItem(item, modifier),
    ),
    serviceItems: normalizeServiceItemList(line),
  };
}

export function getCartLineSelectionSignature(line = {}) {
  return selectionSignature(line);
}

export function getCartLineComboSignature(line = {}) {
  return comboSelectionSignature(line);
}

export function getCartLineIngredientSignature(line = {}) {
  return ingredientSelectionSignature(line);
}

export function getCartLineServiceItemSignature(line = {}) {
  return serviceItemSelectionSignature(line);
}

export function normalizeCartVariant(variant = {}) {
  return canonicalVariant(variant);
}

export function normalizeCartModifier(modifier = {}) {
  return canonicalModifier(modifier);
}

export function normalizeCartServiceItem(serviceItem = {}) {
  return canonicalServiceItem(serviceItem);
}
